import { BaseFeature } from '../../core/base-feature.js';

/**
 * FileBlockFeature - File access blocking (Frontend)
 * Handles: listing blocked files, adding/removing blocks, real-time updates
 */
export class FileBlockFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Block list state
        this.blockedFiles = [];
        this.isLoading = false;
        this.isAvailable = false;

        // Whole-drive lock state (letter hiding)
        this.lockedDrives = [];
        this.systemDrive = 'C:';
        this.driveUpdateCallbacks = new Set();

        // Event listeners for React components
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'file-block',
            name: 'file-block',
            version: 'v1.0',
            capabilities: [
                'list_blocks',
                'add_block',
                'remove_block',
                'check_block',
                'browse_files'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[FileBlock] Initializing...');
        console.log('[FileBlock] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;

        switch (action) {
            case 'list_blocks_response':
                return this.handleListBlocksResponse(payload);

            case 'add_block_response':
                return this.handleAddBlockResponse(payload);

            case 'remove_block_response':
                return this.handleRemoveBlockResponse(payload);

            case 'check_block_response':
                return this.handleCheckBlockResponse(payload);

            case 'browse_files_response':
                return this.handleBrowseFilesResponse(payload);

            case 'verify_blocks_response':
                return this.handleVerifyBlocksResponse(payload);

            case 'reapply_blocks_response':
                return this.handleReapplyBlocksResponse(payload);

            case 'file_block_update':
                // Real-time update from desktop
                return this.handleRealtimeUpdate(payload);

            case 'list_drive_locks_response':
                return this.handleListDriveLocksResponse(payload);

            case 'lock_drive_response':
                return this.handleLockDriveResponse(payload);

            case 'unlock_drive_response':
                return this.handleUnlockDriveResponse(payload);

            case 'drive_locks_update':
                return this.handleDriveLocksRealtimeUpdate(payload);

            default:
                console.warn(`[FileBlock] Unknown action: ${action}`);
        }
    }

    // =====================================
    // Block List Operations
    // =====================================

    /**
     * Request list of blocked files
     */
    listBlocks() {
        this.isLoading = true;
        console.log('[FileBlock] Requesting block list...');
        return this.sendMessage('list_blocks', {});
    }

    handleListBlocksResponse(payload) {
        this.isLoading = false;

        if (!payload.success) {
            console.error('[FileBlock] Failed to get block list:', payload.error);
            return;
        }

        this.blockedFiles = payload.files || [];
        this.isAvailable = payload.available;
        console.log(`[FileBlock] Loaded ${this.blockedFiles.length} blocked files`);

        this.notifyUpdateListeners();
    }

    /**
     * Add a file to the block list
     * @param {string} filePath - Path to block
     * @param {string} reason - Reason for blocking
     * @param {Object} metadata - Additional metadata
     * @returns {Promise<Object>} Result
     */
    async addBlock(filePath, reason = '', metadata = {}) {
        console.log(`[FileBlock] Adding block: ${filePath}`);

        return new Promise((resolve) => {
            const messageId = this.sendMessage('add_block', { filePath, reason, metadata });
            this._pendingRequests.set('add_block', resolve);
        });
    }

    handleAddBlockResponse(payload) {
        const resolve = this._pendingRequests.get('add_block');
        if (resolve) {
            this._pendingRequests.delete('add_block');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[FileBlock] Block added successfully');
            // Request updated list
            this.listBlocks();
        } else {
            console.error('[FileBlock] Failed to add block:', payload.error);
        }
    }

    /**
     * Remove a file from the block list
     * @param {string} filePath - Path to unblock
     * @returns {Promise<Object>} Result
     */
    async removeBlock(filePath) {
        console.log(`[FileBlock] Removing block: ${filePath}`);

        return new Promise((resolve) => {
            const messageId = this.sendMessage('remove_block', { filePath });
            this._pendingRequests.set('remove_block', resolve);
        });
    }

    handleRemoveBlockResponse(payload) {
        const resolve = this._pendingRequests.get('remove_block');
        if (resolve) {
            this._pendingRequests.delete('remove_block');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[FileBlock] Block removed successfully');
            // Request updated list
            this.listBlocks();
        } else {
            console.error('[FileBlock] Failed to remove block:', payload.error);
        }
    }

    /**
     * Check if a file is blocked
     * @param {string} filePath - Path to check
     * @returns {Promise<Object>} Result with blocked status
     */
    async checkBlock(filePath) {
        return new Promise((resolve) => {
            const messageId = this.sendMessage('check_block', { filePath });
            this._pendingRequests.set('check_block', resolve);
        });
    }

    handleCheckBlockResponse(payload) {
        const resolve = this._pendingRequests.get('check_block');
        if (resolve) {
            this._pendingRequests.delete('check_block');
            resolve(payload);
        }
    }

    /**
     * Open file browser dialog to select files to block
     * @returns {Promise<Object>} Result with selected files
     */
    async browseFiles() {
        console.log('[FileBlock] Opening file browser...');

        return new Promise((resolve) => {
            const messageId = this.sendMessage('browse_files', {});
            this._pendingRequests.set('browse_files', resolve);
        });
    }

    handleBrowseFilesResponse(payload) {
        const resolve = this._pendingRequests.get('browse_files');
        if (resolve) {
            this._pendingRequests.delete('browse_files');
            resolve(payload);
        }
    }

    /**
     * Verify all blocks (remove missing files)
     * @returns {Promise<Object>} Result with verification stats
     */
    async verifyBlocks() {
        console.log('[FileBlock] Verifying blocks...');

        return new Promise((resolve) => {
            const messageId = this.sendMessage('verify_blocks', {});
            this._pendingRequests.set('verify_blocks', resolve);
        });
    }

    handleVerifyBlocksResponse(payload) {
        const resolve = this._pendingRequests.get('verify_blocks');
        if (resolve) {
            this._pendingRequests.delete('verify_blocks');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[FileBlock] Verification complete:', payload.results);
            this.listBlocks();
        }
    }

    /**
     * Re-apply all ACLs (useful after system changes)
     * @returns {Promise<Object>} Result with reapply stats
     */
    async reapplyBlocks() {
        console.log('[FileBlock] Re-applying all ACLs...');

        return new Promise((resolve) => {
            const messageId = this.sendMessage('reapply_blocks', {});
            this._pendingRequests.set('reapply_blocks', resolve);
        });
    }

    handleReapplyBlocksResponse(payload) {
        const resolve = this._pendingRequests.get('reapply_blocks');
        if (resolve) {
            this._pendingRequests.delete('reapply_blocks');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[FileBlock] Reapply complete:', payload.results);
        }
    }

    // =====================================
    // Whole-Drive Locks (letter hiding)
    // =====================================

    /**
     * Request the list of locked drives (and which drive is the system drive).
     * @returns {Promise<Object>}
     */
    async listDriveLocks() {
        return new Promise((resolve) => {
            this.sendMessage('list_drive_locks', {});
            this._pendingRequests.set('list_drive_locks', resolve);
        });
    }

    handleListDriveLocksResponse(payload) {
        const resolve = this._pendingRequests.get('list_drive_locks');
        if (resolve) {
            this._pendingRequests.delete('list_drive_locks');
            resolve(payload);
        }
        if (payload && payload.success) {
            this.lockedDrives = payload.drives || [];
            if (payload.systemDrive) this.systemDrive = payload.systemDrive;
            this.notifyDriveListeners();
        }
    }

    /**
     * Lock a whole drive by hiding its letter. The system drive is refused by
     * the desktop guard.
     * @param {string} letter - e.g. "X:"
     * @param {Object} meta - { label, type } for display
     * @returns {Promise<Object>}
     */
    async lockDrive(letter, meta = {}) {
        return new Promise((resolve) => {
            this.sendMessage('lock_drive', { letter, label: meta.label, type: meta.type });
            this._pendingRequests.set('lock_drive', resolve);
        });
    }

    handleLockDriveResponse(payload) {
        const resolve = this._pendingRequests.get('lock_drive');
        if (resolve) {
            this._pendingRequests.delete('lock_drive');
            resolve(payload);
        }
        if (payload && payload.success) {
            this.listDriveLocks();
        }
    }

    /**
     * Unlock a drive (restore its letter).
     * @param {string} letter
     * @returns {Promise<Object>}
     */
    async unlockDrive(letter) {
        return new Promise((resolve) => {
            this.sendMessage('unlock_drive', { letter });
            this._pendingRequests.set('unlock_drive', resolve);
        });
    }

    handleUnlockDriveResponse(payload) {
        const resolve = this._pendingRequests.get('unlock_drive');
        if (resolve) {
            this._pendingRequests.delete('unlock_drive');
            resolve(payload);
        }
        if (payload && payload.success) {
            this.listDriveLocks();
        }
    }

    handleDriveLocksRealtimeUpdate(payload) {
        this.lockedDrives = (payload && payload.drives) || [];
        if (payload && payload.systemDrive) this.systemDrive = payload.systemDrive;
        this.notifyDriveListeners();
    }

    getLockedDrives() {
        return this.lockedDrives;
    }

    isDriveLocked(letter) {
        const norm = String(letter || '').toUpperCase().replace(/[\\/]$/, '');
        return this.lockedDrives.some(d => String(d.letter).toUpperCase() === norm);
    }

    /**
     * Whether a drive is protected (system drive) and can never be locked.
     */
    isDriveProtected(letter) {
        const norm = String(letter || '').toUpperCase().replace(/[\\/]$/, '');
        return norm === String(this.systemDrive).toUpperCase();
    }

    onDriveUpdate(callback) {
        this.driveUpdateCallbacks.add(callback);
        return () => this.driveUpdateCallbacks.delete(callback);
    }

    notifyDriveListeners() {
        const data = { drives: this.lockedDrives, systemDrive: this.systemDrive };
        for (const callback of this.driveUpdateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[FileBlock] Error in drive update callback:', error);
            }
        }
    }

    // =====================================
    // Real-time Updates
    // =====================================

    handleRealtimeUpdate(payload) {
        console.log('[FileBlock] Received real-time update:', payload);
        // Handle both direct files array and nested payload structure
        const files = payload.files || payload.payload?.files || [];
        const count = files.length || payload.count || payload.payload?.count || 0;
        console.log(`[FileBlock] Updating blocked files: ${this.blockedFiles.length} -> ${files.length}`);
        this.blockedFiles = files;
        this.notifyUpdateListeners();
    }

    // =====================================
    // Event Listeners (for React components)
    // =====================================

    /**
     * Subscribe to block list updates
     * @param {Function} callback - Called when block list changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyUpdateListeners() {
        const data = {
            files: this.blockedFiles,
            count: this.blockedFiles.length,
            isLoading: this.isLoading,
            isAvailable: this.isAvailable
        };

        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[FileBlock] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getBlockedFiles() {
        return this.blockedFiles;
    }

    getBlockCount() {
        return this.blockedFiles.length;
    }

    getIsLoading() {
        return this.isLoading;
    }

    getIsAvailable() {
        return this.isAvailable;
    }

    /**
     * Check if a file is blocked (local check)
     */
    isBlocked(filePath) {
        const normalized = filePath.toLowerCase();
        return this.blockedFiles.some(f => f.path.toLowerCase() === normalized);
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[FileBlock] Cleaning up...');
        this.blockedFiles = [];
        this.lockedDrives = [];
        this.updateCallbacks.clear();
        this.driveUpdateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
